import { useEffect, useRef, useState, type FormEvent } from 'react'
import { api, errorMessage } from '../api/client'
import type { Item, SaleView } from '../api/types'
import { DayAverageByItem } from './DayAverageByItem'
import { PersonPicker } from './PersonPicker'
import { Card, Note, NumberInput, PageHead } from './ui'
import { useApi } from '../hooks/useApi'
import { useCustomerPicker, useItemPicker } from '../hooks/usePickers'
import { formatDate, formatMoney, today } from '../lib/format'

/**
 * Selling by the flower rather than by whose sack it came out of.
 *
 * <p>At the morning rush the counter knows two things: which flower went, and
 * what it went for. Which grower's delivery those five kilos came from is a
 * question nobody can answer standing there - and there is no need to. The
 * flower is chosen from a handful of names; a delivery would mean picking one
 * row out of every open lot in the shop.
 *
 * <p>The grower is still paid for his delivery, at a rate set in the evening
 * against the day's average per flower - the card underneath. That rate is
 * always typed by a person. Nothing here guesses it.
 *
 * <p>This is the only screen in the app that writes a sale. It briefly sat on
 * the lot page as well, which meant two boxes doing one job and a shopkeeper
 * having to remember which one he had used. The lot page keeps the day's average
 * - it needs that to set a grower's rate - and nothing else.
 */
/**
 * The flower the selling form opens on, every time.
 *
 * <p>Matched on the name as the shop types it, so renaming that flower on the
 * چیزیں page quietly turns this off rather than breaking anything - the form
 * then falls back to whatever has been selling. If the shop's main flower ever
 * changes, this is the one line to change with it.
 */
const DEFAULT_FLOWER = 'موتیا'

export function SaleByType({
  title,
  hint,
  onSaved,
}: {
  title?: string
  hint?: string
  /** Lets a page holding its own list of sales refresh it after one is written. */
  onSaved?: () => void
}) {
  const customers = useCustomerPicker()
  const itemPicker = useItemPicker()
  // The same list the picker above already holds, so this costs nothing - and
  // the sale screen is the one opened twenty times a morning.
  const items = useApi<Item[]>('/items', [], { cache: true })

  /*
   * Enough recent sales to find the last price of whichever flower is picked.
   * Sixty covers three or four days at this shop's rate of trade, which reaches
   * back past the common flowers comfortably; a rare one simply falls outside
   * and shows no hint, which is better than showing a stale one.
   */
  const recent = useApi<SaleView[]>('/sales/recent?limit=60')

  const [date, setDate] = useState(today())

  /*
   * Bumped after every save, and passed to the day's-average card so it adds the
   * new sale in.
   *
   * This screen used to keep its own copy of /sales/by-item purely to call
   * reload() on it here. The copy was never read - the card below fetches its
   * own - so the refresh refreshed nothing and the averages sat stale until the
   * date was changed. Worse, that copy asked without checking the role: the
   * route is owner-only, so every time Nizam opened the screen he fired a
   * request the server refused, for data nothing displayed. The card next door
   * already carried a comment about not asking when the answer will be refused;
   * this was the same request, one file over, with the guard missing.
   */
  const [savedCount, setSavedCount] = useState(0)

  /*
   * Cash or credit, said out loud.
   *
   * It used to be expressed by whether the customer box happened to be empty -
   * so the most consequential choice on the form, whether this becomes a debt,
   * was made by clearing a field. Starts on cash, which is what an untouched
   * form has always written.
   */
  const [onCredit, setOnCredit] = useState(false)

  const [customerId, setCustomerId] = useState<string | null>(null)
  const [itemId, setItemId] = useState<string | null>(null)
  const [weight, setWeight] = useState('')
  const [rate, setRate] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const weightRef = useRef<HTMLInputElement>(null)

  const amount = weight && rate ? Math.round(Number(weight) * Number(rate)) : 0

  /*
   * A credit sale now has to name somebody.
   *
   * While "cash" meant "customer box left empty", there was nothing to check:
   * no customer simply meant cash. With the choice said out loud, «اُدھار» with
   * nobody picked is a half-finished sentence - and saving it would quietly
   * write a cash sale, which is the one mistake this toggle exists to stop.
   */
  const canSave = Boolean(date && itemId && Number(weight) > 0 && (!onCredit || customerId))

  /**
   * What this flower last went for, from sales the counter can already see.
   *
   * <p>Deliberately read off /sales/recent, which is listed on this very screen
   * and open to both accounts, rather than the rate-history report, which is
   * owner-only. The hint is for whoever is typing - and that is usually Nizam -
   * so it must not need a permission he does not have, and it must not hand him
   * anything he could not already read two inches further down the page.
   *
   * <p>Rates move hard: موتیا alone went at twenty-eight different prices in one
   * month, between 95 and 1,600. Today's price is the honest anchor when there
   * is one; an older one is shown with its date attached, because a four-day-old
   * rate presented as "last time" is how a stale number gets typed in as today's.
   */
  /**
   * The customer as a card rather than a line in a dropdown.
   *
   * <p>The picker already carries the balance beside every name, so showing it
   * after the choice costs nothing - and it is the figure worth seeing before
   * writing somebody more credit, which is exactly the moment it used to
   * disappear.
   *
   * <p><b>The balance is the owner's.</b> useCustomerPicker asks for balances as
   * Uzair and for plain names as Nizam, because the server refuses them to a
   * counter account. The note is simply absent there, so the card shows the name
   * and nothing else - the gate is the data not arriving, not a style hiding it.
   */
  const chosenCustomer = customerId
    ? customers.people.find((person) => person.id === customerId)
    : undefined

  /**
   * The handful of flowers worth a tap, with the whole list one tap further.
   *
   * <p>Four names cover about six sales in seven here, so a dropdown on every
   * entry is a toll paid for the seventh. Counted from recent sales rather than
   * written down, or a flower that goes out of season would never come back onto
   * the row when it returns.
   *
   * <p>Whatever is currently chosen is always among them, even if it has not
   * sold lately - a chip row where the selected chip is missing reads as nothing
   * being selected at all.
   */
  const quickItems = (() => {
    const list = items.data ?? []
    if (list.length === 0) return []

    const soldCount = new Map<string, number>()
    for (const sale of recent.data ?? []) {
      soldCount.set(sale.itemId, (soldCount.get(sale.itemId) ?? 0) + 1)
    }

    const ranked = [...list].sort(
      (a, b) => (soldCount.get(b.id) ?? 0) - (soldCount.get(a.id) ?? 0),
    )
    // Five, so with «اور» the row lands as two tidy threes on a phone.
    const top = ranked.slice(0, 5)
    if (itemId && !top.some((row) => row.id === itemId)) {
      const chosen = list.find((row) => row.id === itemId)
      if (chosen) top.splice(4, 1, chosen)
    }
    return top
  })()

  const lastSold = itemId
    ? (recent.data ?? []).find((sale) => sale.itemId === itemId)
    : undefined
  const rateHint = lastSold
    ? lastSold.date === today()
      ? `آج ${lastSold.itemName} ${formatMoney(lastSold.rate)} پر گیا`
      : `پچھلی بار (${formatDate(lastSold.date)}) ${formatMoney(lastSold.rate)}`
    : null

  function pickItem(id: string | null) {
    setItemId(id)
    // Fills the usual price, but stays editable - prices move through the day
    // and a discount is just a lower number typed here.
    const item = items.data?.find((row) => row.id === id)
    if (item) setRate(item.defaultRate)
  }

  /**
   * Opens on موتیا, with its usual rate already in.
   *
   * <p>Pinned by name rather than worked out, because the shop asked for one
   * answer every time. Computing it was tried: "whichever has sold most lately"
   * does land on موتیا here - it is about half of everything that goes out - but
   * on an odd day it would quietly become something else, and a form that greets
   * you differently on Tuesday is a form you stop trusting to be ready.
   *
   * <p>The name sitting in the code is the price of that, and it is paid
   * carefully: if no flower is called this any more, the old reasoning takes
   * over below rather than the form opening on nothing.
   *
   * <p>It used to open on whichever flower was touched last, which is a worse
   * guess than it sounds: one stray sale of something unusual and the form greets
   * the next hundred entries with it. The flower sold most often over the last
   * few days is the steadier answer - at this shop that is موتیا, which alone is
   * about half of everything that goes out, and it stays موتیا without the name
   * being written into the code. A shop that changes what it sells gets a form
   * that changes with it.
   *
   * <p>Counted off the same recent sales the rate hint uses, so it costs no extra
   * request. Falls back to the last-touched flower before those arrive, and only
   * ever fills a box nobody has touched yet - once the seller picks something the
   * choice is theirs and this stays out of the way.
   */
  useEffect(() => {
    if (itemId !== null) return
    if (!items.data) return

    // The pinned one, if the shop still has a flower by that name.
    const pinned = items.data.find((row) => row.name.trim() === DEFAULT_FLOWER)
    if (pinned) {
      pickItem(pinned.id)
      return
    }

    /*
     * Otherwise whichever flower has gone out most lately - and only once those
     * sales have actually arrived.
     *
     * This effect used to run before they did, find an empty list, fall through
     * to the last-touched flower and set it; by the time the sales landed,
     * itemId was no longer null and the effect returned on its first line. The
     * fallback won every time and the form opened on whatever had been touched
     * last, which is how اسٹر kept appearing while موتیا was outselling it
     * twenty-eight to three.
     */
    if (recent.loading) return

    const soldCount = new Map<string, number>()
    for (const sale of recent.data ?? []) {
      soldCount.set(sale.itemId, (soldCount.get(sale.itemId) ?? 0) + 1)
    }
    const mostSold = [...soldCount.entries()].sort((a, b) => b[1] - a[1])[0]?.[0]

    const first = mostSold ?? itemPicker.recentIds[0]
    if (first && items.data.some((row) => row.id === first)) pickItem(first)
  }, [recent.data, recent.loading, itemPicker.recentIds, items.data, itemId])

  async function save(event: FormEvent) {
    event.preventDefault()
    if (!canSave) return
    setError(null)
    setBusy(true)
    try {
      await api.post('/sales', { date, customerId, itemId, weight, rate })
      setDone(`${customerId ? 'اُدھار' : 'نقد'} بکری محفوظ — ${formatMoney(amount)}`)
      // Keep the date, the buyer, the flower and the rate for the next bag; clear
      // only the weight, which is the one thing that changes sale to sale.
      setWeight('')
      weightRef.current?.focus()
      setSavedCount((n) => n + 1)
      // So the hint under the rate box knows about the sale just written.
      recent.reload()
      // The customer list carries every balance and is the heaviest thing this
      // screen loads. A cash sale moves nobody's balance, so refetching it after
      // one is pure waiting - and the rush is exactly when that is felt.
      if (customerId) customers.reload()
      onSaved?.()
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  if ((items.data?.length ?? 0) === 0 && !items.loading) {
    return (
      <Card>
        <Note kind="warn">
          پہلے <strong>چیزیں</strong> والے صفحے میں پھولوں کے نام لکھیں، پھر یہاں بکری لکھ سکیں گے۔
        </Note>
      </Card>
    )
  }

  /**
   * The date as a chip beside the page's name, not a labelled row in the form.
   *
   * <p>Nine sales in ten are today's, so a full field spent a whole line on a
   * question nobody is asking - and the day a sale belongs to is what the whole
   * screen is about, so it reads better at the top than buried among the boxes.
   *
   * <p>It is still a real date input underneath. Tapping it opens the phone's
   * own picker and back-dating a slip written a day late works exactly as
   * before; styling the input itself rather than triggering it from a button is
   * deliberate, because showPicker() is not supported everywhere and a chip that
   * did nothing on the shop's phone would be worse than the row it replaced.
   */
  const dateChip = (
    <label className="datechip">
      <span className="datechip__face">
        {date === today() ? `آج — ${formatDate(date)}` : formatDate(date)}
      </span>
      <input
        type="date"
        value={date}
        onChange={(e) => setDate(e.target.value)}
        /*
         * For engines with no stretched calendar button of their own.
         * Must run inside the tap to count as a user gesture, and is
         * wrapped because calling it where it is unsupported - or twice,
         * when the native indicator has already opened the picker -
         * throws rather than doing nothing.
         */
        onClick={(event) => {
          const input = event.currentTarget as HTMLInputElement & { showPicker?: () => void }
          try {
            input.showPicker?.()
          } catch {
            /* The native indicator has it. */
          }
        }}
        required
      />
    </label>
  )

  return (
    <>
      {title && <PageHead title={title} hint={hint} aside={dateChip} />}

      <Card title={title ? undefined : 'بکری لکھیں — قسم کے حساب سے'}>
        {error && <Note kind="error">{error}</Note>}
        {done && <Note kind="info">{done}</Note>}

        <form onSubmit={save}>

          {/*
            Cash or credit, chosen rather than implied. Switching to cash drops
            the customer with it - leaving a name attached to a sale that is not
            on credit is a contradiction the form should not be able to hold.
          */}
          <div className="pay-switch" role="group" aria-label="نقد یا اُدھار">
            <button
              type="button"
              className={`pay-switch__btn${onCredit ? '' : ' is-active'}`}
              aria-pressed={!onCredit}
              onClick={() => {
                setOnCredit(false)
                setCustomerId(null)
              }}
            >
              نقد
            </button>
            <button
              type="button"
              className={`pay-switch__btn pay-switch__btn--credit${onCredit ? ' is-active' : ''}`}
              aria-pressed={onCredit}
              onClick={() => setOnCredit(true)}
            >
              اُدھار
            </button>
          </div>

          {/*
            The chosen customer, with what he already owes.

            A name in a closed dropdown says nothing. The balance is the reason
            to hesitate before adding to it, and it belongs where the decision is
            being made. Owner only - see chosenCustomer.
          */}
          {onCredit && (
            <div className="buyer">
              {chosenCustomer ? (
                <>
                  <span className="buyer__initial">{chosenCustomer.name.trim().charAt(0)}</span>
                  <span className="buyer__who">
                    <b>{chosenCustomer.name}</b>
                    {chosenCustomer.note && (
                      <span className={`buyer__due${chosenCustomer.noteTone === 'due' ? ' is-due' : ''}`}>
                        بقایا <span className="amt">{chosenCustomer.note}</span>
                      </span>
                    )}
                  </span>
                </>
              ) : (
                <span className="buyer__who muted">گاہک چنیں</span>
              )}

              <PersonPicker
                label=""
                triggerLabel="بدلیں"
                value={customerId}
                onChange={setCustomerId}
                people={customers.people}
                recentIds={customers.recentIds}
                loading={customers.loading}
              />
            </div>
          )}

          {/*
            The flowers that actually move, as chips. «اور» is the whole list,
            so nothing is out of reach - it is one tap further rather than one
            tap always.
          */}
          <p className="field-head">پھول</p>
          <div className="chips">
            {quickItems.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`chip${item.id === itemId ? ' is-on' : ''}`}
                onClick={() => pickItem(item.id)}
              >
                {item.name}
              </button>
            ))}
            <span className="chip chip--more">
              <PersonPicker
                label=""
                noun="پھول"
                triggerLabel="+ اور"
                value={itemId}
                onChange={pickItem}
                people={itemPicker.people}
                recentIds={itemPicker.recentIds}
                loading={itemPicker.loading}
              />
            </span>
          </div>

          {/* The only two figures anybody types, so the biggest things here. */}
          <div className="two-up">
            <label className="big-field">
              <span className="field-head">وزن (کلو)</span>
              <input
                ref={weightRef}
                inputMode="decimal"
                value={weight}
                placeholder="0.000"
                onChange={(e) => setWeight(e.target.value.replace(/[^\d.]/g, ''))}
                required
              />
            </label>

            <label className="big-field">
              <span className="field-head">ریٹ (فی کلو)</span>
              <NumberInput value={rate} onChange={setRate} placeholder="0" />
            </label>
          </div>

          {/* What it last went for, so today's number has something to sit against. */}
          {rateHint && <p className="muted small rate-hint">{rateHint}</p>}

          {/*
            The amount, as a statement rather than a box.

            It was a greyed-out input, which looks like somewhere to type and is
            the one figure on this form nobody may type. Showing the working
            beside it is what lets a wrong weight be caught before saving.
          */}
          <div className="sale-total">
            <span>
              <span className="sale-total__label">کل رقم</span>
              {weight && rate && (
                <span className="sale-total__work">
                  <span className="amt">
                    {weight} × {rate}
                  </span>{' '}
                  کلو
                </span>
              )}
            </span>
            <span className="sale-total__value">{formatMoney(amount)}</span>
          </div>

          <button className="btn btn--save" disabled={!canSave || busy}>
            {busy ? 'لکھ رہے ہیں…' : 'بکری محفوظ کریں'}
          </button>

          {onCredit && !customerId && (
            <p className="muted small" style={{ marginTop: 8, textAlign: 'center' }}>
              پہلے گاہک چنیں
            </p>
          )}
        </form>
      </Card>

      <DayAverageByItem date={date} refreshKey={savedCount} />

    </>
  )
}
